import { useCallback, useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { SCHEMAS } from "@/pages/admin/schemas";
import { FieldInput } from "@/pages/admin/Fields";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const Editor = ({ coll, item, onClose, onSaved }) => {
  const schema = SCHEMAS[coll];
  const [f, setF] = useState(item);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (f.id) await api.put(`/admin/content/${coll}/${f.id}`, f);
      else await api.post(`/admin/content/${coll}`, f);
      toast.success("Saved");
      onSaved();
    } catch (er) { toast.error(errMsg(er)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#070B16] border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="content-editor-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl text-white">{f.id ? "Edit" : "New"} {schema.label.replace(/s$/, "").toLowerCase()}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-5">
          {schema.fields.map((fd) => (
            <div key={fd.k} className={fd.type === "bool" ? "flex items-center justify-between" : ""}>
              <label className="label" htmlFor={fd.k}>{fd.label}</label>
              <FieldInput f={fd} value={f[fd.k]} onChange={(v) => setF((p) => ({ ...p, [fd.k]: v }))} />
            </div>
          ))}
          <div className="flex gap-3 sticky bottom-0 bg-[#070B16] py-4">
            <button disabled={busy} className="btn-gold" data-testid="editor-save-btn">{busy ? "Saving..." : "Save"}</button>
            <button type="button" onClick={onClose} className="btn-outline">Cancel</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function CollectionManager({ coll }) {
  const schema = SCHEMAS[coll];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => api.get(`/admin/content/${coll}`).then((r) => setItems(r.data)), [coll]);
  useEffect(() => { load(); setEditing(null); }, [load]);
  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.title || it.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/content/${coll}/${it.id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const togglePub = async (it) => {
    try { await api.put(`/admin/content/${coll}/${it.id}`, { ...it, published: !it.published }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = items.filter((i) => !q || JSON.stringify([i.title, i.name, i.category]).toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid={`admin-collection-${coll}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <h1 className="font-serif text-3xl font-semibold text-white">{schema.label}</h1>
        <div className="flex gap-3">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="field !py-2 sm:!w-60" data-testid="admin-search-input" />
          <button onClick={() => setEditing({ published: false })} className="btn-gold !py-2 shrink-0" data-testid="admin-new-btn"><Plus size={15} />New</button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-left text-xs uppercase tracking-wider text-slate-400">
            <tr>{schema.columns.map((c) => <th key={c} className="p-3">{c.replace("_", " ")}</th>)}<th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {shown.map((it) => (
              <tr key={it.id} className="border-t border-white/5 text-slate-200 hover:bg-white/[0.03]" data-testid={`admin-row-${it.slug}`}>
                {schema.columns.map((c) => <td key={c} className="p-3 max-w-xs truncate">{String(it[c] ?? "")}</td>)}
                <td className="p-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-1 rounded-full ${it.published ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-slate-400"}`}>{it.published ? "Published" : "Draft"}</span></td>
                <td className="p-3">
                  <div className="flex justify-end gap-3 text-slate-400">
                    <button onClick={() => togglePub(it)} aria-label={it.published ? "Unpublish" : "Publish"} className="hover:text-white" data-testid={`admin-toggle-publish-${it.slug}`}>{it.published ? <EyeOff size={16} /> : <Eye size={16} />}</button>
                    <button onClick={() => setEditing(it)} aria-label="Edit" className="hover:text-white" data-testid={`admin-edit-${it.slug}`}><Pencil size={16} /></button>
                    <button onClick={() => remove(it)} aria-label="Delete" className="hover:text-red-400" data-testid={`admin-delete-${it.slug}`}><Trash2 size={16} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={9} className="p-10 text-center text-slate-500">Nothing here yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {editing && <Editor coll={coll} item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
